import { AddressForm } from "@/components/address/AddressForm";
import { InputNumber } from "@/components/InputNumber";
import { selectStandardProps } from "@/utils/componentUtils";
import { licensePlateNormalizeFunction } from "@/utils/formUtils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { Card, Cascader, Col, Divider, Form, Input, Row, Select } from "antd";
import { DefaultOptionType } from "antd/es/select";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import LabelWithTooltip from "../../../../../../../common/components/form/labels/LabelWithTooltip";
import { rowGutter } from "../../../../../../../common/constants";
import { ClientType } from "../../../../../../client/enums";
import { InsuranceCoverageType } from "../../../../../../contract/enums";
import { InstitutionEnum } from "../../../../../../institution/enums";
import { ClientExperience } from "../../../../../enums";
import { CalcResult } from "../../../../types";
import { filterApplicableSuccessResults } from "../../../../utils";
import { useTravelCalcResults } from "../../../contexts";
import { TravelAbandonedHouseholdType, TravelInsuranceType, TravelPetType } from "../../../enums";
import { TravelCalc, TravelCalcResultData, TravelGenForm } from "../../../types";

const colSpan = 4;
const bigColSpan = 8;

interface Props {
  policyHolderType?: ClientType;
  calcData: TravelCalc;
  selectedResult: CalcResult<TravelCalcResultData>;
}

export const TravelGenOtherDataSection = ({ policyHolderType, calcData, selectedResult }: Props) => {
  const { t } = useTranslation();

  const { calcResultsGroupedByInstitution } = useTravelCalcResults();

  const form = Form.useFormInstance<TravelGenForm>();
  const abandonedHouseholdDataType = Form.useWatch(
    (values) => values.coveragesData?.abandonedHouseholdData?.type,
    form,
  );

  const selectedInstitutionEnum = selectedResult.insuranceInstitution.institutionEnum;
  const policyHolderIsCompany = policyHolderType === ClientType.SELF_EMPLOYED || policyHolderType === ClientType.LEGAL;
  const isShortTermInsuranceType = calcData.generalData.insuranceType === TravelInsuranceType.SHORT_TERM;
  const isUnionYearInsurance =
    calcData.generalData.insuranceType === TravelInsuranceType.YEAR &&
    selectedInstitutionEnum === InstitutionEnum.UNION;

  const isCoverageDetailRequired = (coverageType: InsuranceCoverageType) =>
    !isUnionYearInsurance || !!selectedResult.data?.appliedCoverages.some((coverage) => coverage.type === coverageType);

  return (
    <Card type="inner" className="card-box" title={t("calc.travel.sections.otherData")}>
      {(isShortTermInsuranceType || isUnionYearInsurance) && (
        <>
          {isShortTermInsuranceType &&
            calcData.coveragesData?.abandonedHousehold &&
            selectedInstitutionEnum === InstitutionEnum.UNION && (
              <>
                <Divider className="divider-subheader">
                  {t("calc.travel.attrs.coveragesData.abandonedHouseholdData._label")}
                </Divider>

                <Row gutter={rowGutter}>
                  <Col span={colSpan}>
                    <Form.Item
                      name={["coveragesData", "abandonedHouseholdData", "type"]}
                      label={t("calc.travel.enums.abandonedHouseholdType._label")}
                      rules={[validations.notNull]}
                    >
                      <Select
                        {...selectStandardProps}
                        options={Object.keys(TravelAbandonedHouseholdType).map((type) => ({
                          value: type,
                          label: t("calc.travel.enums.abandonedHouseholdType." + type),
                        }))}
                      />
                    </Form.Item>
                  </Col>

                  {abandonedHouseholdDataType === TravelAbandonedHouseholdType.FLAT && (
                    <>
                      <Col span={colSpan}>
                        <Form.Item
                          name={["coveragesData", "abandonedHouseholdData", "apartmentNumber"]}
                          label={t("calc.travel.attrs.coveragesData.abandonedHouseholdData.apartmentNumber")}
                          rules={[validations.notBlank, validations.size(1, 64)]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>

                      <Col span={colSpan}>
                        <Form.Item
                          name={["coveragesData", "abandonedHouseholdData", "apartmentFloor"]}
                          label={t("calc.travel.attrs.coveragesData.abandonedHouseholdData.apartmentFloor")}
                          rules={[validations.notNull, validations.minNumber(0), validations.maxNumber(40)]}
                        >
                          <InputNumber />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>

                <AddressForm
                  formItemsNamePath={["coveragesData", "abandonedHouseholdData", "address"]}
                  label={t("calc.travel.attrs.coveragesData.abandonedHouseholdData.address._label")}
                  required
                />
              </>
            )}

          {calcData.coveragesData?.vehicleAssistance &&
            (selectedInstitutionEnum === InstitutionEnum.GENERALI ||
              selectedInstitutionEnum === InstitutionEnum.UNION) &&
            isCoverageDetailRequired(InsuranceCoverageType.TRAVEL_VEHICLE_ASSISTANCE) && (
              <>
                <Divider className="divider-subheader">
                  {t("calc.travel.attrs.coveragesData.vehicleAssistanceData._label")}
                </Divider>

                <Row gutter={rowGutter}>
                  <Col span={colSpan}>
                    <Form.Item
                      name={["coveragesData", "vehicleAssistanceData", "licensePlate"]}
                      label={t("calc.travel.attrs.coveragesData.vehicleAssistanceData.licensePlate")}
                      rules={[validations.notBlank, validations.fullLicensePlate]}
                      normalize={licensePlateNormalizeFunction}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}

          {calcData.coveragesData?.pet &&
            (selectedInstitutionEnum === InstitutionEnum.UNION ||
              selectedInstitutionEnum === InstitutionEnum.GENERALI) &&
            isCoverageDetailRequired(InsuranceCoverageType.TRAVEL_PET) && (
              <>
                <Divider className="divider-subheader">{t("calc.travel.attrs.coveragesData.petData._label")}</Divider>

                <Row gutter={rowGutter}>
                  {selectedInstitutionEnum === InstitutionEnum.GENERALI && (
                    <Col span={colSpan}>
                      <Form.Item
                        name={["coveragesData", "petData", "type"]}
                        label={t("calc.travel.enums.petType._label")}
                        rules={[validations.notNull]}
                      >
                        <Select
                          {...selectStandardProps}
                          options={Object.keys(TravelPetType).map((type) => ({
                            value: type,
                            label: t("calc.travel.enums.petType." + type),
                          }))}
                        />
                      </Form.Item>
                    </Col>
                  )}

                  {selectedInstitutionEnum === InstitutionEnum.UNION && (
                    <Col span={colSpan}>
                      <Form.Item
                        name={["coveragesData", "petData", "vaccinationIdentifier"]}
                        label={
                          <LabelWithTooltip
                            label={t("calc.travel.attrs.coveragesData.petData.vaccinationIdentifier")}
                            tooltip={t("calc.travel.helpers.vaccinationIdentifierDesc")}
                          />
                        }
                        rules={[
                          validations.notBlank,
                          validations.length(11),
                          validations.pattern(regexPatterns.vaccinationIdentifierRegex),
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  )}

                  {selectedInstitutionEnum === InstitutionEnum.GENERALI && (
                    <>
                      <Col span={colSpan}>
                        <Form.Item
                          name={["coveragesData", "petData", "chipNumber"]}
                          label={
                            <LabelWithTooltip
                              label={t("calc.travel.attrs.coveragesData.petData.chipNumber")}
                              tooltip={t("calc.travel.helpers.chipNumberDesc")}
                            />
                          }
                          rules={[validations.notBlank, validations.length(15)]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>

                      <Col span={colSpan}>
                        <Form.Item
                          name={["coveragesData", "petData", "yearOfBirth"]}
                          label={
                            <LabelWithTooltip
                              label={t("calc.travel.attrs.coveragesData.petData.yearOfBirth")}
                              tooltip={t("calc.travel.helpers.yearOfBirthDesc")}
                            />
                          }
                          rules={[
                            validations.notNull,
                            validations.minNumber(dayjs().year() - 7),
                            validations.maxNumber(dayjs().year()),
                          ]}
                        >
                          <InputNumber noFormat />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>
              </>
            )}
        </>
      )}

      <Divider className="divider-subheader">{t("calc.travel.sections.financialMediationData")}</Divider>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={["financialMediationData", "clientExperience"]}
            label={t("calc.enums.clientExperience._label")}
            rules={[validations.notNull]}
            initialValue={policyHolderIsCompany ? ClientExperience.SUFFICIENT : undefined}
          >
            <Select
              {...selectStandardProps}
              disabled={policyHolderIsCompany}
              options={(policyHolderIsCompany
                ? [ClientExperience.SUFFICIENT]
                : [ClientExperience.NONE, ClientExperience.MINIMAL]
              ).map((experience) => ({
                value: experience,
                label: t("calc.enums.clientExperience." + experience),
              }))}
            />
          </Form.Item>
        </Col>

        <Col span={bigColSpan}>
          <Form.Item
            name={["financialMediationData", "otherClientRequirements"]}
            label={t("calc.travel.attrs.financialMediationData.otherClientRequirements")}
            rules={[validations.none]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={bigColSpan}>
          <Form.Item
            name={["financialMediationData", "recommendedResult"]}
            label={t("calc.travel.attrs.financialMediationData.recommendedResult")}
            rules={[validations.notNull]}
          >
            <Cascader
              allowClear={false}
              expandTrigger="hover"
              options={calcResultsGroupedByInstitution
                .filter((resultsRow) => filterApplicableSuccessResults(resultsRow).length > 0)
                .map<DefaultOptionType>((resultsRow) => {
                  const results = filterApplicableSuccessResults<TravelCalcResultData>(resultsRow);
                  return {
                    label: results[0]?.insuranceInstitution.name,
                    value: results[0]?.insuranceInstitution.institutionEnum,
                    children: results[0]?.coverage
                      ? results.map<DefaultOptionType>((result) => ({
                          label: `${t("calc.travel.sections.coverage")} ${result.coverage}`,
                          value: result.coverage,
                        }))
                      : undefined,
                  };
                })}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name={["financialMediationData", "recommendationReason"]}
            label={t("calc.travel.attrs.financialMediationData.recommendationReason")}
            rules={[validations.none]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={bigColSpan}>
          <Form.Item
            name={["financialMediationData", "additionalClientStatement"]}
            label={t("calc.travel.attrs.financialMediationData.additionalClientStatement")}
            rules={[validations.none]}
          >
            <Input />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name={["financialMediationData", "additionalSuitabilityStatement"]}
            label={
              <LabelWithTooltip
                label={t("calc.travel.attrs.financialMediationData.additionalSuitabilityStatement")}
                tooltip={t("calc.travel.helpers.additionalSuitabilityStatementDesc")}
              />
            }
            rules={[validations.none]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
};
